import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dropdown, Badge, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from './../Context/AuthContext';
import { BASE_URL } from '../constants';
import moment from 'moment';

const NotificationDropdown = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [visibleCount, setVisibleCount] = useState(5);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/users/notification/${user.id}`);
      const data = await response.json();
      setNotifications(data || []);
      setHasMore((data || []).length > 5);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);



  const handleNotificationClick = async (notificationId, redirectPath = `/notification/${notificationId}`) => {
    try {
      await fetch(`${BASE_URL}/users/mark-read/${notificationId}`, {
        method: 'PATCH',
      });
  
      fetchNotifications();
  
      const notification = notifications.find((notif) => notif._id === notificationId);
      if (!notification) return;
  
      if (user?.isRefugee) {
        if (notification.type === 'task') {
          navigate('/task');
        }
        return;
      }
  
      if (user?.isAdmin) {
        switch (notification.type) {
          case 'task':
           
            navigate('/admin/tasks');
            break;
          case 'message':
            navigate(`/admin/messages/${notification._id}`);
            break;
          default:
            navigate(redirectPath);
            break;
        }
      } else {
        navigate(redirectPath);
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };
  

  const handleViewMore = () => {
    const nextCount = visibleCount + 5;
    setVisibleCount(nextCount);
    if (nextCount >= notifications.length) {
      setHasMore(false);
    }
  };

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  return (
    <Dropdown className="ms-3 notification-wrapper">
      <Dropdown.Toggle
        as="button"
        variant="outline-primary"
        className="p-0 position-relative border-0 bg-transparent shadow-none"
      >
        <FontAwesomeIcon icon={faBell} className="text-white fs-5" />
        {!loading && unreadCount > 0 && (
          <Badge
            bg="danger"
            className="position-absolute top-0 start-100 translate-middle rounded-circle"
            style={{ fontSize: '0.65rem', padding: '0.3em 0.45em' }}
          >
            {unreadCount}
          </Badge>
        )}
      </Dropdown.Toggle>

        <Dropdown.Menu
       className="notification-dropdown mt-2"
       style={{
         minWidth: '300px',
         maxHeight: '400px',
         overflowY: 'auto',
         borderRadius: '12px',
         boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
       }}
     >
        <div className="p-3 border-bottom fw-bold text-uppercase text-muted small">
          Notifications ({notifications.length})
        </div>

        {loading ? (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" />
          </div>
        ) : notifications.length === 0 ? (
          <Dropdown.Item className="text-center py-3">No new notifications</Dropdown.Item>
        ) : (
          notifications.slice(0, visibleCount).map((notification, idx) => (
            <div key={notification._id}>
              <Dropdown.Item
                className="d-flex align-items-start gap-2 px-3 py-2"
                style={{
                  backgroundColor: notification.isRead ? '#f8f9fa' : '#fff',
                  color: notification.isRead ? '#6c757d' : '#212529',
                  cursor: 'pointer',
                }}
                onClick={() => handleNotificationClick(notification._id)}
              >
                <div className="flex-grow-1">
                  <div className="fw-semibold">{notification.message}</div>
                  <small className="text-muted">
                    {moment(notification.createdAt).fromNow()}
                  </small>
                </div>
              </Dropdown.Item>
              {idx < notifications.length - 1 && <div className="border-top mx-3" />}
            </div>
          ))
        )}

        {hasMore && !loading && (
          <div className="text-center py-2">
            <button
              className="btn btn-link btn-sm text-primary text-decoration-none"
              onClick={handleViewMore}
            >
              View more
            </button>
          </div>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default NotificationDropdown;
